import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  LayoutDashboard,
  MessageSquare,
  Users,
  Phone,
  Bell,
  Activity,
  Search,
  Layers,
  RefreshCw,
  ShieldAlert,
  Server,
  Plus,
  Pencil,
  KeyRound,
  Eye,
  EyeOff,
} from 'lucide-react';
import { useI18n } from '../i18n/useI18n';
import * as communauteApi from '../services/adminCommunaute';
import { authService } from '../services/authService';
import {
  PageHeader,
  StatusBadge,
  DonutChart,
  EmptyState,
} from '../components/ui/UIComponents';

// Communauté Admin — données via Worker Admin uniquement

const TABS = [
  { id: 'overview', label: "Vue d'ensemble", icon: LayoutDashboard },
  { id: 'members', label: 'Membres', icon: Users },
  { id: 'spaces', label: 'Espaces', icon: Layers },
  { id: 'activity', label: 'Activité', icon: Activity },
  { id: 'calls', label: 'Appels', icon: Phone },
  { id: 'notifications', label: 'Notifications', icon: Bell },
  { id: 'notify', label: 'Notifier un membre', icon: Bell },
  { id: 'media', label: 'Fournisseurs média', icon: Server },
];

/* Catalogue de secours = copie de src/media/provider-catalog.js (Worker). Le formulaire reste
   utilisable même si la liste échoue ; le catalogue renvoyé par le Worker fait toujours foi. */
const FALLBACK_MEDIA_CATALOG = [
  { providerType: 'livekit', label: 'LiveKit', credentialFields: [
    { key: 'apiKey', label: 'API Key', secret: true, required: true, target: 'credentials' },
    { key: 'apiSecret', label: 'API Secret', secret: true, required: true, target: 'credentials' },
    { key: 'wsUrl', label: 'URL WebSocket', secret: false, required: true, target: 'config' },
  ] },
  { providerType: 'daily', label: 'Daily', credentialFields: [
    { key: 'apiKey', label: 'API Key', secret: true, required: true, target: 'credentials' },
    { key: 'domain', label: 'Domaine Daily', secret: false, required: true, target: 'config' },
  ] },
  { providerType: 'agora', label: 'Agora', credentialFields: [
    { key: 'appId', label: 'App ID', secret: false, required: true, target: 'config' },
    { key: 'certificate', label: 'App Certificate', secret: true, required: true, target: 'credentials' },
  ] },
  { providerType: 'cloudflare_realtime', label: 'Cloudflare Realtime', credentialFields: [
    { key: 'appId', label: 'App ID', secret: false, required: true, target: 'config' },
    { key: 'appSecret', label: 'App Secret', secret: true, required: true, target: 'credentials' },
  ] },
];

/* Aides d'affichage par type (n'ajoutent aucune règle : les champs viennent du catalogue). */
const MEDIA_TYPE_HINTS = {
  livekit: { billingUnit: 'participant_minutes', help: 'LiveKit Cloud : Settings → Keys pour la clé et le secret ; l’URL WebSocket est celle du projet.' },
  daily: { billingUnit: 'participant_minutes', help: 'Daily : Developers → API keys ; le domaine est le sous-domaine de votre équipe.' },
  agora: { billingUnit: 'participant_minutes', help: 'Agora Console : App ID et App Certificate du projet (le certificat doit être activé).' },
  cloudflare_realtime: { billingUnit: 'bytes', help: 'Cloudflare Realtime : App ID et App Secret de l’application.' },
};
const MEDIA_FIELD_HINTS = {
  'livekit.apiKey': { placeholder: 'APIxxxxxxxxxxxx' },
  'livekit.apiSecret': { placeholder: 'secret LiveKit' },
  'livekit.wsUrl': { placeholder: 'wss://mon-projet.livekit.cloud', help: 'Doit commencer par wss:// (ou ws://).' },
  'daily.apiKey': { placeholder: 'clé API Daily' },
  'daily.domain': { placeholder: 'monequipe ou monequipe.daily.co', help: 'Sans https:// ni chemin.' },
  'agora.appId': { placeholder: 'App ID Agora' },
  'agora.certificate': { placeholder: 'App Certificate Agora' },
  'cloudflare_realtime.appId': { placeholder: 'App ID Cloudflare Realtime' },
  'cloudflare_realtime.appSecret': { placeholder: 'App Secret Cloudflare Realtime' },
};
const MEDIA_KEY_RE = /^[a-z0-9][a-z0-9_-]{1,62}$/;

/* Contrôle côté console, identique en esprit à celui du Worker (qui reste l'autorité). */
function checkMediaFieldValue(providerType, f, raw) {
  const v = String(raw || '').trim();
  if (!v) return null;
  if (v.length > 2048) return `${f.label} : valeur trop longue.`;
  if (/\s/.test(v)) return `${f.label} : ne doit contenir aucun espace.`;
  if (providerType === 'livekit' && f.key === 'wsUrl' && !/^wss?:\/\/[^/\s]+/i.test(v)) {
    return `${f.label} : doit commencer par wss:// (ou ws://).`;
  }
  if (providerType === 'daily' && f.key === 'domain' && !/^[a-z0-9][a-z0-9.-]*$/i.test(v)) {
    return `${f.label} : sans https:// ni chemin (ex. monequipe ou monequipe.daily.co).`;
  }
  return null;
}

const HEALTH_LABEL = {
  AVAILABLE: 'Disponible',
  COOLDOWN: 'Cooldown technique',
  QUOTA_EXCEEDED: 'Capacité épuisée',
  DISABLED: 'Désactivé',
};

export default function Community() {
  const { t } = useI18n();
  const [tab, setTab] = useState('overview');
  const [period, setPeriod] = useState('7d');
  const [overviewData, setOverviewData] = useState(null);
  const [contactsStats, setContactsStats] = useState(null);
  const [conversationsStats, setConversationsStats] = useState(null);
  const [overviewLoading, setOverviewLoading] = useState(false);
  const [overviewError, setOverviewError] = useState(null);

  const [members, setMembers] = useState([]);
  const [membersLoading, setMembersLoading] = useState(false);
  const [membersError, setMembersError] = useState(null);
  const [memberSearch, setMemberSearch] = useState('');
  const [memberStatus, setMemberStatus] = useState('');
  const [memberCursor, setMemberCursor] = useState(null);
  const [memberNextCursor, setMemberNextCursor] = useState(null);
  const [memberLimit] = useState(20);
  const [selectedMember, setSelectedMember] = useState(null);
  const [memberDetail, setMemberDetail] = useState(null);
  const [memberDetailLoading, setMemberDetailLoading] = useState(false);
  const [memberDetailError, setMemberDetailError] = useState(null);
  const [memberContacts, setMemberContacts] = useState([]);
  const [contactsLoading, setContactsLoading] = useState(false);
  const [contactsError, setContactsError] = useState(null);
  const [contactStatus, setContactStatus] = useState('ACCEPTED');
  const [contactCursor, setContactCursor] = useState(null);
  const [contactNextCursor, setContactNextCursor] = useState(null);

  /* Espaces / Activité / Appels / Notifications */
  const [spacesList, setSpacesList] = useState([]);
  const [spacesLoading, setSpacesLoading] = useState(false);
  const [spacesError, setSpacesError] = useState(null);
  const [spacesNextCursor, setSpacesNextCursor] = useState(null);
  const [selectedSpaceId, setSelectedSpaceId] = useState(null);
  const [spaceDetail, setSpaceDetail] = useState(null);
  const [spaceDetailLoading, setSpaceDetailLoading] = useState(false);
  const [spaceDetailError, setSpaceDetailError] = useState(null);

  const [activityList, setActivityList] = useState([]);
  const [activityLoading, setActivityLoading] = useState(false);
  const [activityError, setActivityError] = useState(null);
  const [activityType, setActivityType] = useState('');
  const [activityFrom, setActivityFrom] = useState('');
  const [activityTo, setActivityTo] = useState('');

  const [callsList, setCallsList] = useState([]);
  const [callsLoading, setCallsLoading] = useState(false);
  const [callsError, setCallsError] = useState(null);
  const [callsNextCursor, setCallsNextCursor] = useState(null);
  const [callStatus, setCallStatus] = useState('');
  const [callProvider, setCallProvider] = useState('');
  const [callFrom, setCallFrom] = useState('');
  const [callTo, setCallTo] = useState('');
  const [selectedCallId, setSelectedCallId] = useState(null);
  const [callDetail, setCallDetail] = useState(null);
  const [callDetailLoading, setCallDetailLoading] = useState(false);
  const [callDetailError, setCallDetailError] = useState(null);

  const [notifsList, setNotifsList] = useState([]);
  const [notifsLoading, setNotifsLoading] = useState(false);
  const [notifsError, setNotifsError] = useState(null);
  const [notifsNextCursor, setNotifsNextCursor] = useState(null);

  /* Fournisseurs média (instances : provider_type = adaptateur, provider_key = instance) */
  const [mediaProviders, setMediaProviders] = useState([]);
  const [mediaCatalog, setMediaCatalog] = useState(FALLBACK_MEDIA_CATALOG);
  const [mediaSummary, setMediaSummary] = useState([]);
  const [mediaLoading, setMediaLoading] = useState(false);
  const [mediaError, setMediaError] = useState(null);
  const [mediaTypeFilter, setMediaTypeFilter] = useState('');
  const [mediaSearch, setMediaSearch] = useState('');
  const [mediaNextCursor, setMediaNextCursor] = useState(null);

  const [mediaFormOpen, setMediaFormOpen] = useState(false);
  const [mediaEditingId, setMediaEditingId] = useState(null);
  const [mediaForm, setMediaForm] = useState(null);
  const [mediaFormBusy, setMediaFormBusy] = useState(false);
  const [mediaFormErr, setMediaFormErr] = useState(null);

  const [mediaShowSecrets, setMediaShowSecrets] = useState(false);
  const [mediaEditingProvider, setMediaEditingProvider] = useState(null);

  const loadOverview = useCallback(async () => {
    setOverviewLoading(true);
    setOverviewError(null);
    try {
      const [ov, ct, cv] = await Promise.all([
        communauteApi.getCommunauteOverview({ period }),
        communauteApi.getContactsStats(),
        communauteApi.getConversationsStats(),
      ]);
      setOverviewData(ov?.data && (ov.data.conversations || ov.data.messages) ? ov.data : ov);
      setContactsStats(ct?.data && (ct.data.accepted != null || ct.data.blocked != null) ? ct.data : ct);
      setConversationsStats(cv?.data && (cv.data.total != null || cv.data.direct != null) ? cv.data : cv);
    } catch (err) {
      setOverviewData(null);
      setContactsStats(null);
      setConversationsStats(null);
      const msg =
        err?.status === 403
          ? 'Vous n’avez pas l’autorisation de consulter la communauté.'
          : err?.status === 502
            ? 'Le service communauté est temporairement injoignable.'
            : err?.status === 503
              ? 'Le service communauté n’est pas configuré.'
              : err?.message || 'Impossible de charger la vue d’ensemble.';
      setOverviewError(msg);
    } finally {
      setOverviewLoading(false);
    }
  }, [period]);

  useEffect(() => {
    if (tab === 'overview') loadOverview();
  }, [tab, loadOverview]);

  const extractMembers = (res) => {
    if (!res) return [];
    if (Array.isArray(res)) return res;
    if (Array.isArray(res.members)) return res.members;
    if (Array.isArray(res.items)) return res.items;
    if (Array.isArray(res.data)) return res.data;
    if (res.data && Array.isArray(res.data.members)) return res.data.members;
    return [];
  };

  const extractPagination = (res) => {
    const pag = res?.pagination || res?.data?.pagination || {};
    return pag.nextCursor ?? pag.next_cursor ?? null;
  };

  const loadMembers = useCallback(
    async ({ cursor = null, append = false } = {}) => {
      setMembersLoading(true);
      setMembersError(null);
      try {
        const params = { limit: memberLimit };
        if (cursor) params.cursor = cursor;
        if (memberStatus) params.status = memberStatus;
        if (memberSearch.trim()) params.search = memberSearch.trim();
        const res = await communauteApi.getCommunauteMembers(params);
        const list = extractMembers(res);
        setMembers((prev) => (append ? [...prev, ...list] : list));
        setMemberNextCursor(extractPagination(res));
        setMemberCursor(cursor);
      } catch (err) {
        if (!append) setMembers([]);
        setMemberNextCursor(null);
        const msg =
          err?.status === 403
            ? 'Vous n’avez pas l’autorisation de consulter les membres.'
            : err?.message || 'Impossible de charger les membres.';
        setMembersError(msg);
      } finally {
        setMembersLoading(false);
      }
    },
    [memberLimit, memberStatus, memberSearch]
  );

  useEffect(() => {
    if (tab !== 'members') return;
    setMemberCursor(null);
    setMemberNextCursor(null);
    loadMembers({ cursor: null, append: false });
  }, [tab, memberStatus]); // search applied on submit / debounce via button

  const extractList = (res, keys = ['items', 'data']) => {
    if (!res) return [];
    if (Array.isArray(res)) return res;
    for (const k of keys) {
      if (Array.isArray(res[k])) return res[k];
      if (res.data && Array.isArray(res.data[k])) return res.data[k];
    }
    if (Array.isArray(res.spaces)) return res.spaces;
    if (Array.isArray(res.calls)) return res.calls;
    if (Array.isArray(res.notifications)) return res.notifications;
    if (Array.isArray(res.events)) return res.events;
    if (Array.isArray(res.activity)) return res.activity;
    return [];
  };

  const loadSpaces = useCallback(async ({ cursor = null, append = false } = {}) => {
    setSpacesLoading(true);
    setSpacesError(null);
    try {
      const params = { limit: 20 };
      if (cursor) params.cursor = cursor;
      const res = await communauteApi.getSpaces(params);
      const list = extractList(res, ['spaces', 'items']);
      setSpacesList((prev) => (append ? [...prev, ...list] : list));
      setSpacesNextCursor(extractPagination(res));
    } catch (err) {
      if (!append) setSpacesList([]);
      setSpacesNextCursor(null);
      setSpacesError(err?.status === 403 ? 'Accès refusé.' : err?.message || 'Impossible de charger les espaces.');
    } finally {
      setSpacesLoading(false);
    }
  }, []);

  useEffect(() => {
    if (tab === 'spaces') loadSpaces({ cursor: null, append: false });
  }, [tab, loadSpaces]);

  const openSpace = async (id) => {
    setSelectedSpaceId(id);
    setSpaceDetail(null);
    setSpaceDetailError(null);
    setSpaceDetailLoading(true);
    try {
      const res = await communauteApi.getSpace(id);
      const d = res?.data && (res.data.id || res.data.name) ? res.data : res;
      setSpaceDetail(d);
    } catch (err) {
      setSpaceDetail(null);
      setSpaceDetailError(err?.status === 404 ? 'Espace introuvable.' : err?.message || 'Impossible de charger l’espace.');
    } finally {
      setSpaceDetailLoading(false);
    }
  };

  const loadActivity = useCallback(async () => {
    setActivityLoading(true);
    setActivityError(null);
    try {
      const params = {};
      if (activityType) params.type = activityType;
      if (activityFrom) {
        const ms = Date.parse(activityFrom);
        if (!Number.isNaN(ms)) params.from = ms;
      }
      if (activityTo) {
        const ms = Date.parse(activityTo);
        if (!Number.isNaN(ms)) params.to = ms;
      }
      const res = await communauteApi.getActivity(params);
      setActivityList(extractList(res, ['events', 'activity', 'items']));
    } catch (err) {
      setActivityList([]);
      setActivityError(err?.status === 403 ? 'Accès refusé.' : err?.message || 'Impossible de charger l’activité.');
    } finally {
      setActivityLoading(false);
    }
  }, [activityType, activityFrom, activityTo]);

  useEffect(() => {
    if (tab === 'activity') loadActivity();
  }, [tab, loadActivity]);

  const loadCalls = useCallback(async ({ cursor = null, append = false } = {}) => {
    setCallsLoading(true);
    setCallsError(null);
    try {
      const params = { limit: 20 };
      if (cursor) params.cursor = cursor;
      if (callStatus) params.status = callStatus;
      if (callProvider.trim()) params.provider = callProvider.trim();
      if (callFrom) {
        const ms = Date.parse(callFrom);
        if (!Number.isNaN(ms)) params.from = ms;
      }
      if (callTo) {
        const ms = Date.parse(callTo);
        if (!Number.isNaN(ms)) params.to = ms;
      }
      const res = await communauteApi.getCalls(params);
      const list = extractList(res, ['calls', 'items']);
      setCallsList((prev) => (append ? [...prev, ...list] : list));
      setCallsNextCursor(extractPagination(res));
    } catch (err) {
      if (!append) setCallsList([]);
      setCallsNextCursor(null);
      setCallsError(err?.status === 403 ? 'Accès refusé.' : err?.message || 'Impossible de charger les appels.');
    } finally {
      setCallsLoading(false);
    }
  }, [callStatus, callProvider, callFrom, callTo]);

  useEffect(() => {
    if (tab === 'calls') loadCalls({ cursor: null, append: false });
  }, [tab, callStatus]); // other filters via apply button

  const openCall = async (id) => {
    setSelectedCallId(id);
    setCallDetail(null);
    setCallDetailError(null);
    setCallDetailLoading(true);
    try {
      const res = await communauteApi.getCall(id);
      const d = res?.data && (res.data.id || res.data.status) ? res.data : res;
      setCallDetail(d);
    } catch (err) {
      setCallDetail(null);
      setCallDetailError(err?.status === 404 ? 'Appel introuvable.' : err?.message || 'Impossible de charger l’appel.');
    } finally {
      setCallDetailLoading(false);
    }
  };

  const loadNotifs = useCallback(async ({ cursor = null, append = false } = {}) => {
    setNotifsLoading(true);
    setNotifsError(null);
    try {
      const params = { limit: 20 };
      if (cursor) params.cursor = cursor;
      const res = await communauteApi.getCommunauteNotifications(params);
      const list = extractList(res, ['notifications', 'items']);
      setNotifsList((prev) => (append ? [...prev, ...list] : list));
      setNotifsNextCursor(extractPagination(res));
    } catch (err) {
      if (!append) setNotifsList([]);
      setNotifsNextCursor(null);
      setNotifsError(err?.status === 403 ? 'Accès refusé.' : err?.message || 'Impossible de charger les notifications.');
    } finally {
      setNotifsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (tab === 'notifications') loadNotifs({ cursor: null, append: false });
  }, [tab, loadNotifs]);

  /* ---- Fournisseurs média ---- */

  const catalogFor = (providerType) => mediaCatalog.find((c) => c.providerType === providerType) || null;
  const fieldsFor = (providerType, target) =>
    (catalogFor(providerType)?.credentialFields || []).filter((f) => f.target === target);

  const loadMedia = useCallback(
    async ({ cursor = null, append = false } = {}) => {
      setMediaLoading(true);
      setMediaError(null);
      try {
        const params = { limit: 50 };
        if (cursor) params.cursor = cursor;
        if (mediaTypeFilter) params.type = mediaTypeFilter;
        if (mediaSearch.trim()) params.q = mediaSearch.trim();
        const res = await communauteApi.getMediaProviders(params);
        const data = res?.data && Array.isArray(res.data.providers) ? res.data : res || {};
        const list = Array.isArray(data.providers) ? data.providers : [];
        setMediaProviders((prev) => (append ? [...prev, ...list] : list));
        setMediaNextCursor(extractPagination(res));
        if (Array.isArray(data.catalog) && data.catalog.length) setMediaCatalog(data.catalog);
        if (Array.isArray(data.summaryByType)) setMediaSummary(data.summaryByType);
      } catch (err) {
        if (!append) setMediaProviders([]);
        setMediaNextCursor(null);
        setMediaError(
          err?.status === 403
            ? 'Vous n’avez pas l’autorisation de consulter les fournisseurs média.'
            : err?.message || 'Impossible de charger les fournisseurs média.'
        );
      } finally {
        setMediaLoading(false);
      }
    },
    [mediaTypeFilter, mediaSearch]
  );

  useEffect(() => {
    if (tab === 'media') loadMedia({ cursor: null, append: false });
  }, [tab, mediaTypeFilter]); // recherche appliquée via le bouton

  const canManageMedia = (() => {
    const user = authService.getCurrentUser();
    const n = user?.niveau;
    if (n === 1 || n === '1' || (typeof n === 'string' && String(n).toUpperCase() === 'SUPER_ADMIN')) {
      return true;
    }
    const perms = authService.getPermissions() || [];
    return perms.some((perm) => {
      const code = typeof perm === 'string' ? perm : perm?.code || perm?.permission || '';
      return code === 'COMMUNAUTE_MEDIA_MODIFIER';
    });
  })();

  const emptyMediaForm = (providerType) => {
    const config = {};
    const secrets = {};
    fieldsFor(providerType, 'config').forEach((f) => { config[f.key] = ''; });
    fieldsFor(providerType, 'credentials').forEach((f) => { secrets[f.key] = ''; });
    return {
      providerType,
      providerKey: providerType ? `${providerType.replace(/_/g, '-')}-01` : '',
      displayName: catalogFor(providerType)?.label || '',
      priority: 100,
      enabled: false,
      billingUnit: MEDIA_TYPE_HINTS[providerType]?.billingUnit || 'seconds',
      quotaLimit: '',
      operationalMarginPercent: 10,
      config,
      secrets,
    };
  };

  const openCreateMediaForm = () => {
    const firstType = mediaCatalog[0]?.providerType || '';
    setMediaEditingId(null);
    setMediaEditingProvider(null);
    setMediaForm(emptyMediaForm(firstType));
    setMediaFormErr(null);
    setMediaShowSecrets(false);
    setMediaFormOpen(true);
  };

  const openEditMediaForm = (p) => {
    const config = {};
    const secrets = {};
    fieldsFor(p.providerType, 'config').forEach((f) => { config[f.key] = (p.config && p.config[f.key]) || ''; });
    fieldsFor(p.providerType, 'credentials').forEach((f) => { secrets[f.key] = ''; });
    setMediaEditingId(p.id);
    setMediaEditingProvider(p);
    setMediaForm({
      providerType: p.providerType,
      providerKey: p.providerKey,
      displayName: p.displayName || '',
      priority: p.priority ?? 100,
      enabled: !!p.enabled,
      billingUnit: p.billingUnit || 'seconds',
      quotaLimit: p.quotaLimit ?? '',
      operationalMarginPercent: p.operationalMarginPercent ?? 10,
      config,
      secrets,
    });
    setMediaFormErr(null);
    setMediaShowSecrets(false);
    setMediaFormOpen(true);
  };

  const closeMediaForm = () => {
    setMediaFormOpen(false);
    setMediaEditingId(null);
    setMediaEditingProvider(null);
    setMediaForm(null);
    setMediaFormErr(null);
    setMediaShowSecrets(false);
  };

  /* Un seul formulaire : paramètres + configuration + identifiants (secrets) du type choisi.
     1) création/modification de l'instance ; 2) envoi des secrets à la route chiffrée si saisis. */
  const submitMediaForm = async () => {
    if (!mediaForm) return;
    setMediaFormErr(null);
    const type = mediaForm.providerType;
    const cfgFields = fieldsFor(type, 'config');
    const secFields = fieldsFor(type, 'credentials');

    if (!type || !catalogFor(type)) {
      setMediaFormErr('Choisissez un type de fournisseur.');
      return;
    }
    if (!mediaEditingId) {
      const key = mediaForm.providerKey.trim();
      if (!key) { setMediaFormErr('L’identifiant d’instance (providerKey) est obligatoire.'); return; }
      if (!MEDIA_KEY_RE.test(key)) {
        setMediaFormErr('Identifiant d’instance invalide : a-z, 0-9, _ ou -, 2 à 63 caractères (ex. livekit-eu-02).');
        return;
      }
    }

    const configPayload = {};
    for (const f of cfgFields) {
      const v = (mediaForm.config[f.key] || '').trim();
      if (!v) {
        if (f.required) { setMediaFormErr(`Champ requis manquant : ${f.label}`); return; }
        continue;
      }
      const bad = checkMediaFieldValue(type, f, v);
      if (bad) { setMediaFormErr(bad); return; }
      configPayload[f.key] = v;
    }

    const secretsPayload = {};
    for (const f of secFields) {
      const v = (mediaForm.secrets[f.key] || '').trim();
      if (!v) continue;
      const bad = checkMediaFieldValue(type, f, v);
      if (bad) { setMediaFormErr(bad); return; }
      secretsPayload[f.key] = v;
    }
    const secretsTouched = Object.keys(secretsPayload).length > 0;
    if (secretsTouched) {
      const missing = secFields.filter((f) => f.required && !secretsPayload[f.key]).map((f) => f.label);
      if (missing.length) {
        setMediaFormErr(`Les identifiants se remplacent en bloc : renseignez aussi ${missing.join(', ')} (ou videz tous les champs secrets pour conserver les identifiants actuels).`);
        return;
      }
    }
    const hasCreds = secretsTouched || !!mediaEditingProvider?.credentialConfigured;
    if (mediaForm.enabled && !hasCreds) {
      setMediaFormErr('Impossible d’activer une instance sans identifiants : saisissez les champs secrets ou laissez « Activée = Non ».');
      return;
    }
    const quota = mediaForm.quotaLimit === '' || mediaForm.quotaLimit == null ? null : Number(mediaForm.quotaLimit);
    if (quota !== null && (!Number.isFinite(quota) || quota < 0)) {
      setMediaFormErr('Quota invalide : nombre positif ou vide (illimité).');
      return;
    }
    const margin = Number(mediaForm.operationalMarginPercent);
    if (!Number.isFinite(margin) || margin < 0 || margin >= 100) {
      setMediaFormErr('Marge opérationnelle invalide : entre 0 et 99.');
      return;
    }

    const common = {
      displayName: mediaForm.displayName.trim() || undefined,
      priority: Number(mediaForm.priority) || 0,
      enabled: !!mediaForm.enabled,
      billingUnit: mediaForm.billingUnit.trim() || 'seconds',
      quotaLimit: quota,
      operationalMarginPercent: margin,
      config: configPayload,
    };
    const payload = mediaEditingId
      ? { id: mediaEditingId, ...common }
      : { providerType: type, providerKey: mediaForm.providerKey.trim(), ...common };

    setMediaFormBusy(true);
    let savedId = mediaEditingId;
    try {
      const res = await communauteApi.createMediaProvider(payload);
      savedId = mediaEditingId || res?.data?.provider?.id || res?.provider?.id || null;
    } catch (err) {
      setMediaFormErr(
        err?.status === 409
          ? 'Cet identifiant d’instance est déjà utilisé.'
          : err?.message || 'Impossible d’enregistrer ce fournisseur.'
      );
      setMediaFormBusy(false);
      return;
    }

    if (secretsTouched) {
      try {
        if (!savedId) throw new Error('Identifiant de l’instance introuvable dans la réponse.');
        await communauteApi.setMediaProviderCredentials(savedId, { credentials: secretsPayload });
      } catch (err) {
        // L'instance existe désormais : on bascule en modification pour ne pas la recréer (409).
        if (savedId && !mediaEditingId) {
          setMediaEditingId(savedId);
          setMediaEditingProvider({ id: savedId, credentialConfigured: false });
          setMediaForm((f) => ({ ...f, enabled: false }));
        }
        setMediaFormErr(`Instance enregistrée, mais identifiants refusés : ${err?.message || 'erreur inconnue'}`);
        setMediaFormBusy(false);
        await loadMedia({ cursor: null, append: false });
        return;
      }
    }
    setMediaFormBusy(false);
    closeMediaForm();
    await loadMedia({ cursor: null, append: false });
  };

  const openMember = async (id) => {
    setSelectedMember(id);
    setMemberDetail(null);
    setMemberDetailError(null);
    setMemberContacts([]);
    setContactCursor(null);
    setContactNextCursor(null);
    setMemberDetailLoading(true);
    try {
      const res = await communauteApi.getCommunauteMember(id);
      const detail = res?.data && (res.data.id || res.data.user_id || res.data.pseudonym) ? res.data : res;
      setMemberDetail(detail);
      await loadMemberContacts(id, { cursor: null, append: false });
    } catch (err) {
      setMemberDetail(null);
      setMemberDetailError(
        err?.status === 404
          ? 'Membre introuvable.'
          : err?.message || 'Impossible de charger la fiche membre.'
      );
    } finally {
      setMemberDetailLoading(false);
    }
  };

  const extractContacts = (res) => {
    if (!res) return [];
    if (Array.isArray(res)) return res;
    if (Array.isArray(res.contacts)) return res.contacts;
    if (Array.isArray(res.items)) return res.items;
    if (Array.isArray(res.data)) return res.data;
    if (res.data && Array.isArray(res.data.contacts)) return res.data.contacts;
    return [];
  };

  const loadMemberContacts = async (id, { cursor = null, append = false, status } = {}) => {
    setContactsLoading(true);
    setContactsError(null);
    try {
      const params = { limit: 20 };
      if (cursor) params.cursor = cursor;
      const st = status !== undefined ? status : contactStatus;
      if (st) params.status = st;
      const res = await communauteApi.getMemberContacts(id, params);
      const list = extractContacts(res);
      setMemberContacts((prev) => (append ? [...prev, ...list] : list));
      setContactNextCursor(extractPagination(res));
      setContactCursor(cursor);
    } catch (err) {
      if (!append) setMemberContacts([]);
      setContactNextCursor(null);
      setContactsError(err?.message || 'Impossible de charger les contacts.');
    } finally {
      setContactsLoading(false);
    }
  };


  const activityIcon = {
    message: MessageSquare,
    conversation: Users,
    space: Layers,
    call: Phone,
    notification: Bell,
  };

  const canNotify = (() => {
    const user = authService.getCurrentUser();
    const n = user?.niveau;
    if (n === 1 || n === '1' || (typeof n === 'string' && String(n).toUpperCase() === 'SUPER_ADMIN')) {
      return true;
    }
    const perms = authService.getPermissions() || [];
    return perms.some((perm) => {
      const code = typeof perm === 'string' ? perm : perm?.code || perm?.permission || '';
      return code === 'COMMUNAUTE_NOTIFY';
    });
  })();

  const [notifyForm, setNotifyForm] = useState({ memberId: '', type: 'message.created' });
  const [notifyBusy, setNotifyBusy] = useState(false);
  const [notifyMsg, setNotifyMsg] = useState(null);
  const [notifyErr, setNotifyErr] = useState(null);

  const runNotify = async () => {
    setNotifyErr(null);
    setNotifyMsg(null);
    const memberId = notifyForm.memberId.trim();
    if (!memberId) {
      setNotifyErr('L’identifiant du membre est obligatoire.');
      return;
    }
    const allowed = ['message.created', 'call.incoming', 'space.joined', 'internal'];
    if (!allowed.includes(notifyForm.type)) {
      setNotifyErr('Type de notification non autorisé.');
      return;
    }
    setNotifyBusy(true);
    try {
      await communauteApi.notifyMember({ memberId, type: notifyForm.type });
      setNotifyMsg('Notification envoyée.');
      setNotifyForm({ memberId: '', type: 'message.created' });
    } catch (err) {
      const msg =
        err?.status === 403
          ? 'Vous n’avez pas l’autorisation d’envoyer une notification.'
          : err?.message || 'L’envoi de la notification a échoué.';
      setNotifyErr(msg);
    } finally {
      setNotifyBusy(false);
    }
  };

  return (
    <div className="space-y-6 fade-up">
      <PageHeader
        title={t("pages.community.title")}
        subtitle={t("pages.community.subtitle")}
      />

      <div className="flex flex-wrap gap-1 bg-white border border-slate-200 p-1.5 rounded-2xl">
        {TABS.map((item) => {
          const Icon = item.icon;
          return (
            <button
              key={item.id}
              onClick={() => setTab(item.id)}
              className={`px-3 py-2 rounded-xl text-xs font-semibold flex items-center gap-1.5 transition-all ${
                tab === item.id ? 'bg-indigo-600 text-white shadow-sm' : 'text-slate-500 hover:text-slate-900 hover:bg-slate-50'
              }`}
            >
              <Icon size={14} /> {item.labelKey ? t(item.labelKey) : item.label}
            </button>
          );
        })}
      </div>

      {/* OVERVIEW */}
      {tab === 'overview' && (
        <div className="space-y-4">
          <div className="flex flex-wrap gap-1 items-center">
            {[
              { id: 'today', label: "Aujourd'hui" },
              { id: '7d', label: '7 jours' },
              { id: '30d', label: '30 jours' },
              { id: 'all', label: 'Tout' },
            ].map((p) => (
              <button
                key={p.id}
                type="button"
                onClick={() => setPeriod(p.id)}
                className={`px-3 py-1.5 rounded-xl text-[11px] font-semibold border ${
                  period === p.id
                    ? 'bg-slate-900 text-white border-slate-900'
                    : 'bg-white text-slate-600 border-slate-200'
                }`}
              >
                {p.label}
              </button>
            ))}
            <button
              type="button"
              className="dsi-btn-secondary text-xs ml-auto"
              onClick={loadOverview}
              disabled={overviewLoading}
            >
              <RefreshCw size={14} className={overviewLoading ? 'animate-spin' : ''} /> Actualiser
            </button>
          </div>

          {overviewError && (
            <div className="p-3 rounded-2xl bg-rose-50 border border-rose-200 text-rose-800 text-xs font-bold flex justify-between gap-2">
              <span className="flex items-center gap-2"><ShieldAlert size={14} /> {overviewError}</span>
              <button type="button" className="underline" onClick={loadOverview}>Réessayer</button>
            </div>
          )}

          {overviewLoading && !overviewData ? (
            <div className="dsi-card p-10 text-center text-xs text-slate-500">Chargement de la vue d’ensemble…</div>
          ) : !overviewData && !overviewError ? (
            <EmptyState title="Aucune donnée" message="La vue d’ensemble n’a renvoyé aucune information." onRetry={loadOverview} />
          ) : overviewData ? (
            <>
              <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-3">
                {[
                  { label: 'Conversations', value: overviewData.conversations?.total },
                  { label: 'Messages', value: overviewData.messages?.total },
                  { label: 'Espaces', value: overviewData.spaces?.total },
                  { label: 'Membres (total)', value: overviewData.members?.total },
                  { label: 'Membres actifs', value: overviewData.members?.active },
                  { label: 'Membres incomplets', value: overviewData.members?.incomplete },
                  { label: 'Membres désactivés', value: overviewData.members?.disabled },
                  { label: 'Contacts acceptés', value: overviewData.contacts?.accepted ?? contactsStats?.accepted },
                  { label: 'Contacts en attente', value: overviewData.contacts?.pending ?? contactsStats?.pending },
                  { label: 'Contacts bloqués', value: overviewData.contacts?.blocked ?? contactsStats?.blocked },
                  { label: 'Appels', value: overviewData.calls?.total },
                  { label: 'Notifications', value: overviewData.notifications?.total },
                  { label: 'Notifs non lues', value: overviewData.notifications?.unread },
                ].map((k) => (
                  <div key={k.label} className="dsi-card p-4">
                    <p className="text-[10px] font-semibold uppercase text-slate-500">{k.label}</p>
                    <p className="text-2xl font-bold text-slate-900 mt-1">
                      {k.value == null ? '—' : Number(k.value).toLocaleString('fr-FR')}
                    </p>
                  </div>
                ))}
              </div>

              <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                <div className="dsi-card p-5">
                  <h3 className="text-sm font-semibold text-slate-900 mb-1">Répartition des conversations</h3>
                  <p className="text-xs text-slate-500 mb-3">Direct · Groupe · Espace · Officiel</p>
                  <DonutChart
                    segments={[
                      { label: 'Direct', value: Number(overviewData.conversations?.direct ?? conversationsStats?.direct) || 0, color: '#4f46e5' },
                      { label: 'Groupe', value: Number(overviewData.conversations?.group ?? conversationsStats?.group) || 0, color: '#10b981' },
                      { label: 'Espace', value: Number(overviewData.conversations?.space ?? conversationsStats?.space) || 0, color: '#f59e0b' },
                      { label: 'Officiel', value: Number(overviewData.conversations?.official ?? conversationsStats?.official) || 0, color: '#0ea5e9' },
                    ]}
                    centerValue={overviewData.conversations?.total ?? conversationsStats?.total ?? '—'}
                    centerLabel="Total"
                  />
                </div>
                <div className="dsi-card p-5">
                  <h3 className="text-sm font-semibold text-slate-900 mb-1">Réseau / contacts</h3>
                  <p className="text-xs text-slate-500 mb-3">Acceptés · En attente · Bloqués</p>
                  <DonutChart
                    segments={[
                      { label: 'Acceptés', value: Number(overviewData.contacts?.accepted ?? contactsStats?.accepted) || 0, color: '#10b981' },
                      { label: 'En attente', value: Number(overviewData.contacts?.pending ?? contactsStats?.pending) || 0, color: '#f59e0b' },
                      { label: 'Bloqués', value: Number(overviewData.contacts?.blocked ?? contactsStats?.blocked) || 0, color: '#f43f5e' },
                    ]}
                    centerValue={
                      (Number(overviewData.contacts?.accepted ?? contactsStats?.accepted) || 0) +
                      (Number(overviewData.contacts?.pending ?? contactsStats?.pending) || 0) +
                      (Number(overviewData.contacts?.blocked ?? contactsStats?.blocked) || 0) || '—'
                    }
                    centerLabel="Contacts"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                <div className="dsi-card p-5">
                  <h3 className="text-sm font-semibold text-slate-900 mb-1">Appels</h3>
                  <DonutChart
                    segments={[
                      { label: 'Connectés', value: Number(overviewData.calls?.connected) || 0, color: '#10b981' },
                      { label: 'Terminés', value: Number(overviewData.calls?.ended) || 0, color: '#64748b' },
                      { label: 'Sans réponse', value: Number(overviewData.calls?.noAnswer) || 0, color: '#f59e0b' },
                      { label: 'Refusés', value: Number(overviewData.calls?.declined) || 0, color: '#f43f5e' },
                      { label: 'Échoués', value: Number(overviewData.calls?.failed) || 0, color: '#e11d48' },
                    ]}
                    centerValue={overviewData.calls?.total ?? '—'}
                    centerLabel="Appels"
                  />
                </div>
                <div className="dsi-card p-5">
                  <h3 className="text-sm font-semibold text-slate-900 mb-1">Membres (tous temps)</h3>
                  <p className="text-xs text-slate-500 mb-3">Indépendant de la période sélectionnée</p>
                  <DonutChart
                    segments={[
                      { label: 'Actifs', value: Number(overviewData.members?.active) || 0, color: '#10b981' },
                      { label: 'Incomplets', value: Number(overviewData.members?.incomplete) || 0, color: '#f59e0b' },
                      { label: 'Désactivés', value: Number(overviewData.members?.disabled) || 0, color: '#94a3b8' },
                    ]}
                    centerValue={overviewData.members?.total ?? '—'}
                    centerLabel="Membres"
                  />
                </div>
              </div>
            </>
          ) : null}
        </div>
      )}

      {/* CONVERSATIONS */}

      {tab === 'members' && (
        <div className="space-y-4">
          <div className="flex flex-wrap gap-2 items-center">
            <div className="relative flex-1 min-w-[180px] max-w-sm">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                className="dsi-input pl-9 text-xs"
                placeholder="Identifiant ou nom / pseudonyme"
                value={memberSearch}
                onChange={(e) => setMemberSearch(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    setMemberCursor(null);
                    loadMembers({ cursor: null, append: false });
                  }
                }}
              />
            </div>
            <select
              className="dsi-input text-xs w-auto"
              value={memberStatus}
              onChange={(e) => setMemberStatus(e.target.value)}
            >
              <option value="">Tous les statuts</option>
              <option value="ACTIVE">Actif</option>
              <option value="INCOMPLETE">Incomplet</option>
              <option value="DISABLED">Désactivé</option>
            </select>
            <button
              type="button"
              className="dsi-btn-secondary text-xs"
              onClick={() => {
                setMemberCursor(null);
                loadMembers({ cursor: null, append: false });
              }}
              disabled={membersLoading}
            >
              Rechercher
            </button>
            <button
              type="button"
              className="dsi-btn-secondary text-xs"
              onClick={() => {
                setMemberCursor(null);
                loadMembers({ cursor: null, append: false });
              }}
              disabled={membersLoading}
            >
              <RefreshCw size={14} className={membersLoading ? 'animate-spin' : ''} /> Actualiser
            </button>
          </div>

          {membersError && (
            <div className="p-3 rounded-2xl bg-rose-50 border border-rose-200 text-rose-800 text-xs font-bold flex justify-between gap-2">
              <span className="flex items-center gap-2"><ShieldAlert size={14} /> {membersError}</span>
              <button type="button" className="underline" onClick={() => loadMembers({ cursor: null, append: false })}>Réessayer</button>
            </div>
          )}

          {membersLoading && !members.length ? (
            <div className="dsi-card p-10 text-center text-xs text-slate-500">Chargement des membres…</div>
          ) : !members.length && !membersError ? (
            <EmptyState title="Aucun membre" message="Aucun membre ne correspond aux critères." onRetry={() => loadMembers({ cursor: null, append: false })} />
          ) : (
            <div className="dsi-card overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-left text-sm">
                  <thead className="bg-slate-50 text-[11px] uppercase text-slate-500 font-semibold border-b border-slate-200">
                    <tr>
                      <th className="px-4 py-3">Identifiant</th>
                      <th className="px-4 py-3">Nom / pseudonyme</th>
                      <th className="px-4 py-3">Statut</th>
                      <th className="px-4 py-3">Contacts</th>
                      <th className="px-4 py-3">Bloqués</th>
                      <th className="px-4 py-3" />
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {members.map((m) => {
                      const id = m.id || m.user_id || m.userId;
                      const name = m.display_name || m.displayName || m.pseudonym || m.name || '—';
                      const status = m.status || '—';
                      return (
                        <tr key={id} className="hover:bg-slate-50/80">
                          <td className="px-4 py-3 font-mono text-xs">{id}</td>
                          <td className="px-4 py-3">{name}</td>
                          <td className="px-4 py-3">
                            <StatusBadge
                              statusKey={
                                String(status).toUpperCase() === 'ACTIVE'
                                  ? 'ACTIF'
                                  : String(status).toUpperCase() === 'DISABLED'
                                    ? 'INACTIF'
                                    : 'BROUILLON'
                              }
                              text={status}
                            />
                          </td>
                          <td className="px-4 py-3">{m.contactsCount ?? m.contacts_count ?? '—'}</td>
                          <td className="px-4 py-3">{m.blockedCount ?? m.blocked_count ?? '—'}</td>
                          <td className="px-4 py-3 text-right">
                            <button type="button" className="dsi-btn-secondary text-[11px] py-1.5" onClick={() => openMember(id)}>
                              Voir
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              {memberNextCursor && (
                <div className="p-3 border-t border-slate-100 flex justify-center">
                  <button
                    type="button"
                    className="dsi-btn-secondary text-xs"
                    disabled={membersLoading}
                    onClick={() => loadMembers({ cursor: memberNextCursor, append: true })}
                  >
                    {membersLoading ? 'Chargement…' : 'Charger plus'}
                  </button>
                </div>
              )}
            </div>
          )}

          {selectedMember && (
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
              <div className="absolute inset-0 bg-slate-900/40" onClick={() => setSelectedMember(null)} />
              <div className="relative w-full max-w-2xl max-h-[90vh] overflow-y-auto dsi-card p-6 space-y-4">
                <div className="flex items-start justify-between gap-2">
                  <h3 className="font-bold text-slate-900">Fiche membre</h3>
                  <button type="button" className="dsi-btn-secondary text-xs" onClick={() => setSelectedMember(null)}>Fermer</button>
                </div>
                {memberDetailLoading ? (
                  <p className="text-xs text-slate-500">Chargement…</p>
                ) : memberDetailError ? (
                  <div className="p-3 rounded-2xl bg-rose-50 border border-rose-200 text-rose-800 text-xs font-bold flex gap-2">
                    <ShieldAlert size={14} /> {memberDetailError}
                  </div>
                ) : memberDetail ? (
                  <div className="space-y-2 text-sm">
                    <p><span className="text-slate-500 text-xs font-semibold uppercase">Identifiant</span><br />
                      <span className="font-mono text-xs">{memberDetail.id || memberDetail.user_id || selectedMember}</span></p>
                    <p><span className="text-slate-500 text-xs font-semibold uppercase">Nom / pseudonyme</span><br />
                      {memberDetail.display_name || memberDetail.displayName || memberDetail.pseudonym || memberDetail.name || '—'}</p>
                    <p><span className="text-slate-500 text-xs font-semibold uppercase">Statut</span><br />{memberDetail.status || '—'}</p>
                    <p><span className="text-slate-500 text-xs font-semibold uppercase">Contacts / bloqués</span><br />
                      {memberDetail.contactsCount ?? memberDetail.contacts_count ?? '—'} · {memberDetail.blockedCount ?? memberDetail.blocked_count ?? '—'}</p>
                  </div>
                ) : null}

                <div className="border-t border-slate-100 pt-4 space-y-3">
                  <div className="flex flex-wrap items-center gap-2 justify-between">
                    <h4 className="text-sm font-semibold">Contacts</h4>
                    <select
                      className="dsi-input text-xs w-auto"
                      value={contactStatus}
                      onChange={(e) => {
                        const v = e.target.value;
                        setContactStatus(v);
                        if (selectedMember) {
                          setContactCursor(null);
                          loadMemberContacts(selectedMember, { cursor: null, append: false, status: v });
                        }
                      }}
                    >
                      <option value="ACCEPTED">Acceptés</option>
                      <option value="PENDING">En attente</option>
                      <option value="BLOCKED">Bloqués</option>
                    </select>
                  </div>
                  {contactsError && (
                    <p className="text-xs text-rose-700 font-semibold">{contactsError}</p>
                  )}
                  {contactsLoading && !memberContacts.length ? (
                    <p className="text-xs text-slate-500">Chargement des contacts…</p>
                  ) : !memberContacts.length ? (
                    <p className="text-xs text-slate-500">Aucun contact pour ce filtre.</p>
                  ) : (
                    <ul className="space-y-2 max-h-48 overflow-y-auto">
                      {memberContacts.map((c, i) => {
                        const cid = c.id || c.user_id || c.peerId || i;
                        const cname = c.display_name || c.displayName || c.pseudonym || c.name || c.peer?.display_name || '—';
                        return (
                          <li key={cid} className="flex justify-between text-xs border border-slate-100 rounded-xl px-3 py-2">
                            <span className="font-mono">{c.user_id || c.peerId || cid}</span>
                            <span>{cname}</span>
                            <span className="text-slate-500">{c.status || contactStatus}</span>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                  {contactNextCursor && (
                    <button
                      type="button"
                      className="dsi-btn-secondary text-xs"
                      disabled={contactsLoading}
                      onClick={() => loadMemberContacts(selectedMember, { cursor: contactNextCursor, append: true })}
                    >
                      Charger plus de contacts
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      )}



      {/* SPACES */}
      {tab === 'spaces' && (
        <div className="space-y-4">
          {spacesError && (
            <div className="p-3 rounded-2xl bg-rose-50 border border-rose-200 text-rose-800 text-xs font-bold flex justify-between gap-2">
              <span className="flex items-center gap-2"><ShieldAlert size={14} /> {spacesError}</span>
              <button type="button" className="underline" onClick={() => loadSpaces({ cursor: null, append: false })}>Réessayer</button>
            </div>
          )}
          <div className="flex justify-end">
            <button type="button" className="dsi-btn-secondary text-xs" disabled={spacesLoading} onClick={() => loadSpaces({ cursor: null, append: false })}>
              <RefreshCw size={14} className={spacesLoading ? 'animate-spin' : ''} /> Actualiser
            </button>
          </div>
          {spacesLoading && !spacesList.length ? (
            <div className="dsi-card p-10 text-center text-xs text-slate-500">Chargement des espaces…</div>
          ) : !spacesList.length && !spacesError ? (
            <EmptyState title="Aucun espace" message="Aucun espace n’est disponible." onRetry={() => loadSpaces({ cursor: null, append: false })} />
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {spacesList.map((s) => {
                const id = s.id || s.spaceId;
                return (
                  <button key={id} type="button" className="dsi-card p-4 text-left hover:border-indigo-300" onClick={() => openSpace(id)}>
                    <p className="text-sm font-semibold text-slate-900">{s.name || s.title || id}</p>
                    <p className="text-xs text-slate-500 mt-1">
                      {[s.membersCount ?? s.members_count, s.memberCount].find((x) => x != null) != null
                        ? `${s.membersCount ?? s.members_count ?? s.memberCount} membres`
                        : '—'}
                    </p>
                  </button>
                );
              })}
            </div>
          )}
          {spacesNextCursor && (
            <div className="flex justify-center">
              <button type="button" className="dsi-btn-secondary text-xs" disabled={spacesLoading} onClick={() => loadSpaces({ cursor: spacesNextCursor, append: true })}>
                Charger plus
              </button>
            </div>
          )}
          {selectedSpaceId && (
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
              <div className="absolute inset-0 bg-slate-900/40" onClick={() => setSelectedSpaceId(null)} />
              <div className="relative w-full max-w-lg max-h-[90vh] overflow-y-auto dsi-card p-6 space-y-3">
                <div className="flex justify-between items-start">
                  <h3 className="font-bold">Détail de l’espace</h3>
                  <button type="button" className="dsi-btn-secondary text-xs" onClick={() => setSelectedSpaceId(null)}>Fermer</button>
                </div>
                {spaceDetailLoading ? (
                  <p className="text-xs text-slate-500">Chargement…</p>
                ) : spaceDetailError ? (
                  <p className="text-xs text-rose-700 font-semibold">{spaceDetailError}</p>
                ) : spaceDetail ? (
                  <div className="text-sm space-y-2">
                    <p><span className="text-xs uppercase text-slate-500 font-semibold">Identifiant</span><br /><span className="font-mono text-xs">{spaceDetail.id || selectedSpaceId}</span></p>
                    <p><span className="text-xs uppercase text-slate-500 font-semibold">Nom</span><br />{spaceDetail.name || spaceDetail.title || '—'}</p>
                    {spaceDetail.description != null && <p><span className="text-xs uppercase text-slate-500 font-semibold">Description</span><br />{spaceDetail.description}</p>}
                    {Array.isArray(spaceDetail.members) && spaceDetail.members.length > 0 && (
                      <div>
                        <p className="text-xs uppercase text-slate-500 font-semibold mb-1">Membres</p>
                        <ul className="space-y-1 max-h-40 overflow-y-auto text-xs">
                          {spaceDetail.members.map((m, i) => (
                            <li key={m.id || m.user_id || i} className="flex justify-between border border-slate-100 rounded-lg px-2 py-1">
                              <span className="font-mono">{m.user_id || m.id || '—'}</span>
                              <span>{m.role || '—'}</span>
                              <span className="text-slate-400">{m.joinedAt || m.joined_at || m.date || ''}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                ) : null}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ACTIVITY */}
      {tab === 'activity' && (
        <div className="space-y-4">
          <div className="flex flex-wrap gap-2 items-end">
            <label className="text-xs space-y-1">
              <span className="font-semibold text-slate-600">Type</span>
              <input className="dsi-input text-xs" value={activityType} onChange={(e) => setActivityType(e.target.value)} placeholder="ex. message" />
            </label>
            <label className="text-xs space-y-1">
              <span className="font-semibold text-slate-600">Du</span>
              <input className="dsi-input text-xs" type="datetime-local" value={activityFrom} onChange={(e) => setActivityFrom(e.target.value)} />
            </label>
            <label className="text-xs space-y-1">
              <span className="font-semibold text-slate-600">Au</span>
              <input className="dsi-input text-xs" type="datetime-local" value={activityTo} onChange={(e) => setActivityTo(e.target.value)} />
            </label>
            <button type="button" className="dsi-btn-secondary text-xs" disabled={activityLoading} onClick={loadActivity}>Appliquer</button>
          </div>
          {activityError && (
            <div className="p-3 rounded-2xl bg-rose-50 border border-rose-200 text-rose-800 text-xs font-bold flex gap-2">
              <ShieldAlert size={14} /> {activityError}
            </div>
          )}
          {activityLoading && !activityList.length ? (
            <div className="dsi-card p-10 text-center text-xs text-slate-500">Chargement de l’activité…</div>
          ) : !activityList.length && !activityError ? (
            <EmptyState title="Aucune activité" message="Aucun événement pour ces critères." onRetry={loadActivity} />
          ) : (
            <div className="dsi-card p-5">
              <div className="relative border-l-2 border-slate-200 ml-3 space-y-4 max-h-[28rem] overflow-y-auto">
                {activityList.map((e, i) => {
                  const kind = e.type || e.kind || '';
                  const Icon = activityIcon[kind] || Activity;
                  const when = e.createdAt || e.created_at || e.at || e.timestamp;
                  const label = e.text || e.message || e.summary || kind || 'Événement';
                  return (
                    <div key={e.id || i} className="relative pl-6">
                      <span className="absolute -left-[11px] top-0 w-5 h-5 rounded-full bg-indigo-50 border border-indigo-200 flex items-center justify-center">
                        <Icon size={12} className="text-indigo-600" />
                      </span>
                      <p className="text-[11px] text-slate-400 font-mono">
                        {when != null ? (typeof when === 'number' ? new Date(when).toLocaleString('fr-FR') : String(when)) : '—'}
                      </p>
                      <p className="text-sm text-slate-800 font-medium">{label}</p>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}

      {/* CALLS */}
      {tab === 'calls' && (
        <div className="space-y-4">
          <div className="flex flex-wrap gap-2 items-end">
            <label className="text-xs space-y-1">
              <span className="font-semibold text-slate-600">Statut</span>
              <input className="dsi-input text-xs" value={callStatus} onChange={(e) => setCallStatus(e.target.value)} placeholder="ex. ended" />
            </label>
            <label className="text-xs space-y-1">
              <span className="font-semibold text-slate-600">Provider</span>
              <input className="dsi-input text-xs" value={callProvider} onChange={(e) => setCallProvider(e.target.value)} />
            </label>
            <label className="text-xs space-y-1">
              <span className="font-semibold text-slate-600">Du</span>
              <input className="dsi-input text-xs" type="datetime-local" value={callFrom} onChange={(e) => setCallFrom(e.target.value)} />
            </label>
            <label className="text-xs space-y-1">
              <span className="font-semibold text-slate-600">Au</span>
              <input className="dsi-input text-xs" type="datetime-local" value={callTo} onChange={(e) => setCallTo(e.target.value)} />
            </label>
            <button type="button" className="dsi-btn-secondary text-xs" disabled={callsLoading} onClick={() => loadCalls({ cursor: null, append: false })}>Appliquer</button>
          </div>
          {callsError && (
            <div className="p-3 rounded-2xl bg-rose-50 border border-rose-200 text-rose-800 text-xs font-bold flex justify-between gap-2">
              <span className="flex items-center gap-2"><ShieldAlert size={14} /> {callsError}</span>
              <button type="button" className="underline" onClick={() => loadCalls({ cursor: null, append: false })}>Réessayer</button>
            </div>
          )}
          {callsLoading && !callsList.length ? (
            <div className="dsi-card p-10 text-center text-xs text-slate-500">Chargement des appels…</div>
          ) : !callsList.length && !callsError ? (
            <EmptyState title="Aucun appel" message="Aucun appel pour ces critères." onRetry={() => loadCalls({ cursor: null, append: false })} />
          ) : (
            <div className="dsi-card overflow-hidden">
              <table className="w-full text-left text-sm">
                <thead className="bg-slate-50 text-[11px] uppercase text-slate-500 font-semibold border-b border-slate-200">
                  <tr>
                    <th className="px-4 py-3">Identifiant</th>
                    <th className="px-4 py-3">Statut</th>
                    <th className="px-4 py-3">Provider</th>
                    <th className="px-4 py-3">Date</th>
                    <th className="px-4 py-3" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {callsList.map((c) => {
                    const id = c.id || c.callId;
                    const when = c.createdAt || c.created_at || c.at;
                    return (
                      <tr key={id} className="hover:bg-slate-50/80">
                        <td className="px-4 py-3 font-mono text-xs">{id}</td>
                        <td className="px-4 py-3">{c.status || '—'}</td>
                        <td className="px-4 py-3">{c.provider || '—'}</td>
                        <td className="px-4 py-3 text-xs">{when != null ? (typeof when === 'number' ? new Date(when).toLocaleString('fr-FR') : String(when)) : '—'}</td>
                        <td className="px-4 py-3 text-right">
                          <button type="button" className="dsi-btn-secondary text-[11px] py-1.5" onClick={() => openCall(id)}>Voir</button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {callsNextCursor && (
                <div className="p-3 border-t flex justify-center">
                  <button type="button" className="dsi-btn-secondary text-xs" disabled={callsLoading} onClick={() => loadCalls({ cursor: callsNextCursor, append: true })}>Charger plus</button>
                </div>
              )}
            </div>
          )}
          {selectedCallId && (
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
              <div className="absolute inset-0 bg-slate-900/40" onClick={() => setSelectedCallId(null)} />
              <div className="relative w-full max-w-lg max-h-[90vh] overflow-y-auto dsi-card p-6 space-y-3">
                <div className="flex justify-between">
                  <h3 className="font-bold">Détail de l’appel</h3>
                  <button type="button" className="dsi-btn-secondary text-xs" onClick={() => setSelectedCallId(null)}>Fermer</button>
                </div>
                {callDetailLoading ? (
                  <p className="text-xs text-slate-500">Chargement…</p>
                ) : callDetailError ? (
                  <p className="text-xs text-rose-700 font-semibold">{callDetailError}</p>
                ) : callDetail ? (
                  <div className="text-sm space-y-2">
                    <p><span className="text-xs uppercase text-slate-500 font-semibold">Identifiant</span><br /><span className="font-mono text-xs">{callDetail.id || selectedCallId}</span></p>
                    <p><span className="text-xs uppercase text-slate-500 font-semibold">Statut</span><br />{callDetail.status || '—'}</p>
                    <p><span className="text-xs uppercase text-slate-500 font-semibold">Provider</span><br />{callDetail.provider || '—'}</p>
                    {callDetail.providerUsageEstimate != null && (
                      <p><span className="text-xs uppercase text-slate-500 font-semibold">Estimation d’usage</span><br />
                        {callDetail.providerUsageEstimate} {callDetail.providerUsageUnit || ''}</p>
                    )}
                    {Array.isArray(callDetail.providerAttempts) && (
                      <div>
                        <p className="text-xs uppercase text-slate-500 font-semibold mb-1">Tentatives provider</p>
                        <ul className="text-xs space-y-1">
                          {callDetail.providerAttempts.map((a, i) => (
                            <li key={i} className="border border-slate-100 rounded px-2 py-1 text-xs">
                              {typeof a === 'object'
                                ? [a.status, a.provider, a.at, a.message].filter(Boolean).map(String).join(' · ') || 'Tentative'
                                : String(a)}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                ) : null}
              </div>
            </div>
          )}
        </div>
      )}

      {/* NOTIFICATIONS */}
      {tab === 'notifications' && (
        <div className="space-y-4">
          {notifsError && (
            <div className="p-3 rounded-2xl bg-rose-50 border border-rose-200 text-rose-800 text-xs font-bold flex justify-between gap-2">
              <span className="flex items-center gap-2"><ShieldAlert size={14} /> {notifsError}</span>
              <button type="button" className="underline" onClick={() => loadNotifs({ cursor: null, append: false })}>Réessayer</button>
            </div>
          )}
          <div className="flex justify-end">
            <button type="button" className="dsi-btn-secondary text-xs" disabled={notifsLoading} onClick={() => loadNotifs({ cursor: null, append: false })}>
              <RefreshCw size={14} className={notifsLoading ? 'animate-spin' : ''} /> Actualiser
            </button>
          </div>
          {notifsLoading && !notifsList.length ? (
            <div className="dsi-card p-10 text-center text-xs text-slate-500">Chargement des notifications…</div>
          ) : !notifsList.length && !notifsError ? (
            <EmptyState title="Aucune notification" message="Aucune notification à afficher." onRetry={() => loadNotifs({ cursor: null, append: false })} />
          ) : (
            <div className="dsi-card overflow-hidden">
              <table className="w-full text-left text-sm">
                <thead className="bg-slate-50 text-[11px] uppercase text-slate-500 font-semibold border-b border-slate-200">
                  <tr>
                    <th className="px-4 py-3">Identifiant</th>
                    <th className="px-4 py-3">Membre</th>
                    <th className="px-4 py-3">Type</th>
                    <th className="px-4 py-3">Lu</th>
                    <th className="px-4 py-3">Créée le</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {notifsList.map((n) => (
                    <tr key={n.id}>
                      <td className="px-4 py-3 font-mono text-xs">{n.id}</td>
                      <td className="px-4 py-3 font-mono text-xs">{n.memberId || n.member_id || '—'}</td>
                      <td className="px-4 py-3">{n.type || '—'}</td>
                      <td className="px-4 py-3">{n.read === true ? 'Oui' : n.read === false ? 'Non' : '—'}</td>
                      <td className="px-4 py-3 text-xs">
                        {n.createdAt != null
                          ? (typeof n.createdAt === 'number' ? new Date(n.createdAt).toLocaleString('fr-FR') : String(n.createdAt))
                          : n.created_at != null
                            ? String(n.created_at)
                            : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {notifsNextCursor && (
                <div className="p-3 border-t flex justify-center">
                  <button type="button" className="dsi-btn-secondary text-xs" disabled={notifsLoading} onClick={() => loadNotifs({ cursor: notifsNextCursor, append: true })}>Charger plus</button>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* CONTACTS */}
{tab === 'notify' && (
        <div className="space-y-4 max-w-xl">
          {!canNotify ? (
            <div className="dsi-card p-5 text-xs text-slate-600">
              Vous n’avez pas l’autorisation d’envoyer une notification.
            </div>
          ) : (
            <div className="dsi-card p-5 space-y-3">
              <h3 className="text-sm font-bold text-slate-900">Envoyer une notification</h3>
              <p className="text-xs text-slate-500">Notification administrative à un membre. Ce n’est pas une messagerie.</p>
              {notifyErr && (
                <div className="p-3 rounded-2xl bg-rose-50 border border-rose-200 text-rose-800 text-xs font-bold flex gap-2">
                  <ShieldAlert size={14} /> {notifyErr}
                </div>
              )}
              {notifyMsg && (
                <div className="p-3 rounded-2xl bg-emerald-50 border border-emerald-200 text-emerald-800 text-xs font-bold">
                  {notifyMsg}
                </div>
              )}
              <label className="block text-xs space-y-1">
                <span className="font-semibold text-slate-600">Identifiant membre</span>
                <input
                  className="dsi-input"
                  value={notifyForm.memberId}
                  onChange={(e) => setNotifyForm({ ...notifyForm, memberId: e.target.value })}
                  disabled={notifyBusy}
                  placeholder="Identifiant du membre"
                />
              </label>
              <label className="block text-xs space-y-1">
                <span className="font-semibold text-slate-600">Type</span>
                <select
                  className="dsi-input"
                  value={notifyForm.type}
                  onChange={(e) => setNotifyForm({ ...notifyForm, type: e.target.value })}
                  disabled={notifyBusy}
                >
                  <option value="message.created">message.created</option>
                  <option value="call.incoming">call.incoming</option>
                  <option value="space.joined">space.joined</option>
                  <option value="internal">internal</option>
                </select>
              </label>
              <button type="button" className="dsi-btn-primary text-xs" disabled={notifyBusy || !canNotify} onClick={runNotify}>
                {notifyBusy ? 'Envoi…' : 'Envoyer la notification'}
              </button>
            </div>
          )}
        </div>
      )}



      {/* MEDIA */}
      {tab === 'media' && (
        <div className="space-y-4">
          <div className="flex flex-wrap gap-2 items-end">
            <label className="text-xs space-y-1">
              <span className="font-semibold text-slate-600">Type</span>
              <select className="dsi-input text-xs" value={mediaTypeFilter} onChange={(e) => setMediaTypeFilter(e.target.value)}>
                <option value="">Tous les types</option>
                {mediaCatalog.map((c) => (
                  <option key={c.providerType} value={c.providerType}>
                    {c.label}
                  </option>
                ))}
              </select>
            </label>
            <label className="text-xs space-y-1">
              <span className="font-semibold text-slate-600">Recherche (clé / nom)</span>
              <input className="dsi-input text-xs" value={mediaSearch} onChange={(e) => setMediaSearch(e.target.value)} placeholder="ex. livekit-eu" />
            </label>
            <button type="button" className="dsi-btn-secondary text-xs" disabled={mediaLoading} onClick={() => loadMedia({ cursor: null, append: false })}>
              Appliquer
            </button>
            {canManageMedia && (
              <button type="button" className="dsi-btn-primary text-xs flex items-center gap-1 ml-auto" onClick={openCreateMediaForm}>
                <Plus size={14} /> Nouvelle instance
              </button>
            )}
          </div>

          {mediaSummary.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {mediaSummary.map((s) => (
                <div key={s.providerType} className="dsi-card px-3 py-2 text-xs">
                  <span className="font-semibold text-slate-700">{catalogFor(s.providerType)?.label || s.providerType}</span>
                  <span className="text-slate-500"> — {s.enabled ?? 0}/{s.total ?? 0} activées</span>
                </div>
              ))}
            </div>
          )}

          {mediaError && (
            <div className="p-3 rounded-2xl bg-rose-50 border border-rose-200 text-rose-800 text-xs font-bold flex justify-between gap-2">
              <span className="flex items-center gap-2"><ShieldAlert size={14} /> {mediaError}</span>
              <button type="button" className="underline" onClick={() => loadMedia({ cursor: null, append: false })}>Réessayer</button>
            </div>
          )}

          {mediaLoading && !mediaProviders.length ? (
            <div className="dsi-card p-10 text-center text-xs text-slate-500">Chargement des fournisseurs média…</div>
          ) : !mediaProviders.length && !mediaError ? (
            <EmptyState title="Aucune instance" message="Aucun fournisseur média ne correspond aux critères." onRetry={() => loadMedia({ cursor: null, append: false })} />
          ) : (
            <div className="dsi-card overflow-hidden">
              <table className="w-full text-left text-sm">
                <thead className="bg-slate-50 text-[11px] uppercase text-slate-500 font-semibold border-b border-slate-200">
                  <tr>
                    <th className="px-4 py-3">Type</th>
                    <th className="px-4 py-3">Instance (providerKey)</th>
                    <th className="px-4 py-3">Nom</th>
                    <th className="px-4 py-3">Priorité</th>
                    <th className="px-4 py-3">Activée</th>
                    <th className="px-4 py-3">Santé</th>
                    <th className="px-4 py-3">Identifiants</th>
                    <th className="px-4 py-3" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {mediaProviders.map((p) => (
                    <tr key={p.id} className="hover:bg-slate-50/80">
                      <td className="px-4 py-3 text-xs font-semibold">{catalogFor(p.providerType)?.label || p.providerType}</td>
                      <td className="px-4 py-3 font-mono text-xs">{p.providerKey}</td>
                      <td className="px-4 py-3 text-xs">{p.displayName || '—'}</td>
                      <td className="px-4 py-3 text-xs">{p.priority}</td>
                      <td className="px-4 py-3">
                        <StatusBadge statusKey={p.enabled ? 'ACTIF' : 'INACTIF'} text={p.enabled ? 'Activée' : 'Désactivée'} />
                      </td>
                      <td className="px-4 py-3 text-xs">{HEALTH_LABEL[p.healthStatus] || p.healthStatus || '—'}</td>
                      <td className="px-4 py-3">
                        <StatusBadge
                          statusKey={p.credentialConfigured ? 'ACTIF' : 'BROUILLON'}
                          text={p.credentialConfigured ? 'Configurés' : 'Non configurés'}
                        />
                      </td>
                      <td className="px-4 py-3 text-right whitespace-nowrap">
                        {canManageMedia && (
                          <>
                            <button type="button" className="dsi-btn-secondary text-[11px] py-1.5 mr-1.5" onClick={() => openEditMediaForm(p)}>
                              <Pencil size={12} className="inline -mt-0.5 mr-1" /> Modifier / identifiants
                            </button>
                          </>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {mediaNextCursor && (
                <div className="p-3 border-t flex justify-center">
                  <button type="button" className="dsi-btn-secondary text-xs" disabled={mediaLoading} onClick={() => loadMedia({ cursor: mediaNextCursor, append: true })}>
                    Charger plus
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Création / modification d'une instance */}
          {mediaFormOpen && mediaForm && (
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
              <div className="absolute inset-0 bg-slate-900/40" onClick={closeMediaForm} />
              <div className="relative w-full max-w-lg max-h-[90vh] overflow-y-auto dsi-card p-6 space-y-3">
                <div className="flex justify-between">
                  <h3 className="font-bold">{mediaEditingId ? 'Modifier l’instance' : 'Nouvelle instance'}</h3>
                  <button type="button" className="dsi-btn-secondary text-xs" onClick={closeMediaForm}>Fermer</button>
                </div>
                {mediaFormErr && (
                  <div className="p-3 rounded-2xl bg-rose-50 border border-rose-200 text-rose-800 text-xs font-bold flex gap-2">
                    <ShieldAlert size={14} /> {mediaFormErr}
                  </div>
                )}
                <label className="block text-xs space-y-1">
                  <span className="font-semibold text-slate-600">Type de fournisseur (adaptateur)</span>
                  <select
                    className="dsi-input"
                    value={mediaForm.providerType}
                    disabled={!!mediaEditingId}
                    onChange={(e) => setMediaForm(emptyMediaForm(e.target.value))}
                  >
                    {mediaCatalog.map((c) => (
                      <option key={c.providerType} value={c.providerType}>{c.label}</option>
                    ))}
                  </select>
                  {mediaEditingId && <span className="text-[11px] text-slate-400">Le type d’une instance existante ne peut pas être changé.</span>}
                </label>
                <label className="block text-xs space-y-1">
                  <span className="font-semibold text-slate-600">Identifiant d’instance (providerKey)</span>
                  <input
                    className="dsi-input"
                    value={mediaForm.providerKey}
                    disabled={!!mediaEditingId}
                    onChange={(e) => setMediaForm({ ...mediaForm, providerKey: e.target.value })}
                    placeholder="ex. livekit-eu-02"
                  />
                </label>
                <label className="block text-xs space-y-1">
                  <span className="font-semibold text-slate-600">Nom affiché</span>
                  <input className="dsi-input" value={mediaForm.displayName} onChange={(e) => setMediaForm({ ...mediaForm, displayName: e.target.value })} />
                </label>
                <div className="grid grid-cols-2 gap-3">
                  <label className="block text-xs space-y-1">
                    <span className="font-semibold text-slate-600">Priorité</span>
                    <input type="number" className="dsi-input" value={mediaForm.priority} onChange={(e) => setMediaForm({ ...mediaForm, priority: e.target.value })} />
                  </label>
                  <label className="block text-xs space-y-1 flex-1">
                    <span className="font-semibold text-slate-600">Activée</span>
                    <select className="dsi-input" value={mediaForm.enabled ? '1' : '0'} onChange={(e) => setMediaForm({ ...mediaForm, enabled: e.target.value === '1' })}>
                      <option value="0">Non</option>
                      <option value="1">Oui</option>
                    </select>
                  </label>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <label className="block text-xs space-y-1">
                    <span className="font-semibold text-slate-600">Unité native</span>
                    <input
                      className="dsi-input"
                      value={mediaForm.billingUnit}
                      onChange={(e) => setMediaForm({ ...mediaForm, billingUnit: e.target.value })}
                      placeholder="seconds, participant_minutes…"
                    />
                    <span className="text-[11px] text-slate-400">Doit correspondre à l’unité facturée par ce fournisseur — voir sa documentation.</span>
                  </label>
                  <label className="block text-xs space-y-1">
                    <span className="font-semibold text-slate-600">Marge opérationnelle (%)</span>
                    <input type="number" className="dsi-input" value={mediaForm.operationalMarginPercent} onChange={(e) => setMediaForm({ ...mediaForm, operationalMarginPercent: e.target.value })} />
                  </label>
                </div>
                <label className="block text-xs space-y-1">
                  <span className="font-semibold text-slate-600">Quota (dans l’unité native ci-dessus)</span>
                  <input className="dsi-input" value={mediaForm.quotaLimit} onChange={(e) => setMediaForm({ ...mediaForm, quotaLimit: e.target.value })} placeholder="vide = illimité" />
                </label>
                {MEDIA_TYPE_HINTS[mediaForm.providerType]?.help && (
                  <p className="text-[11px] text-slate-500 bg-slate-50 rounded-xl px-3 py-2">{MEDIA_TYPE_HINTS[mediaForm.providerType].help}</p>
                )}
                {fieldsFor(mediaForm.providerType, 'config').length > 0 && (
                  <div className="space-y-2 pt-2 border-t border-slate-100">
                    <p className="text-xs font-semibold text-slate-600">Configuration — {catalogFor(mediaForm.providerType)?.label} <span className="font-normal text-slate-400">(non secrète)</span></p>
                    {fieldsFor(mediaForm.providerType, 'config').map((f) => {
                      const hint = MEDIA_FIELD_HINTS[`${mediaForm.providerType}.${f.key}`] || {};
                      return (
                        <label key={f.key} className="block text-xs space-y-1">
                          <span className="text-slate-600">{f.label}{f.required ? ' *' : ''}</span>
                          <input
                            className="dsi-input"
                            autoComplete="off"
                            spellCheck={false}
                            placeholder={hint.placeholder || ''}
                            value={mediaForm.config[f.key] || ''}
                            onChange={(e) => setMediaForm({ ...mediaForm, config: { ...mediaForm.config, [f.key]: e.target.value } })}
                          />
                          {hint.help && <span className="text-[11px] text-slate-400">{hint.help}</span>}
                        </label>
                      );
                    })}
                  </div>
                )}
                {fieldsFor(mediaForm.providerType, 'credentials').length > 0 && (
                  <div className="space-y-2 pt-2 border-t border-slate-100">
                    <div className="flex items-center justify-between">
                      <p className="text-xs font-semibold text-slate-600 flex items-center gap-1">
                        <KeyRound size={12} /> Identifiants — {catalogFor(mediaForm.providerType)?.label} <span className="font-normal text-slate-400">(chiffrés, jamais réaffichés)</span>
                      </p>
                      <button type="button" className="text-[11px] text-slate-500 flex items-center gap-1" onClick={() => setMediaShowSecrets((v) => !v)}>
                        {mediaShowSecrets ? <EyeOff size={12} /> : <Eye size={12} />} {mediaShowSecrets ? 'Masquer' : 'Afficher'}
                      </button>
                    </div>
                    {mediaEditingId && (
                      <p className="text-[11px] text-slate-500">
                        {mediaEditingProvider?.credentialConfigured
                          ? 'Identifiants actuels : configurés. Laissez tous les champs vides pour les conserver, ou renseignez-les tous pour les remplacer.'
                          : 'Aucun identifiant configuré pour cette instance.'}
                      </p>
                    )}
                    {fieldsFor(mediaForm.providerType, 'credentials').map((f) => {
                      const hint = MEDIA_FIELD_HINTS[`${mediaForm.providerType}.${f.key}`] || {};
                      return (
                        <label key={f.key} className="block text-xs space-y-1">
                          <span className="text-slate-600">{f.label}{f.required ? ' *' : ''}</span>
                          <input
                            type={mediaShowSecrets ? 'text' : 'password'}
                            autoComplete="new-password"
                            spellCheck={false}
                            className="dsi-input"
                            value={mediaForm.secrets[f.key] || ''}
                            onChange={(e) => setMediaForm({ ...mediaForm, secrets: { ...mediaForm.secrets, [f.key]: e.target.value } })}
                            placeholder={mediaEditingProvider?.credentialConfigured ? '•••••••• (inchangé)' : hint.placeholder || ''}
                          />
                          {hint.help && <span className="text-[11px] text-slate-400">{hint.help}</span>}
                        </label>
                      );
                    })}
                  </div>
                )}
                <button type="button" className="dsi-btn-primary text-xs" disabled={mediaFormBusy} onClick={submitMediaForm}>
                  {mediaFormBusy ? 'Enregistrement…' : mediaEditingId ? 'Enregistrer les modifications' : 'Créer l’instance'}
                </button>
              </div>
            </div>
          )}

        </div>
      )}

      {/* SETTINGS */}
    </div>
  );
}
